import { ReactNode, useId, useState } from 'react';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import cn from 'classnames';
import { InfoIcon } from '@phosphor-icons/react';
import { useTranslation } from 'next-i18next';
import IconWrapper from '@components/ui/Icons';
import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';

import styles from './Tooltip.module.scss';

export interface TooltipProps {
  content: ReactNode;
  className?: string;
  popupClassName?: string;
  contentClassName?: string;
  id?: string;
  iconSize?: number;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  mobileSide?: 'top' | 'right' | 'bottom' | 'left';
  mobileAlign?: 'start' | 'center' | 'end';
  mobileSideOffset?: number;
  showArrow?: boolean;
  children?: ReactNode;
}

const Tooltip = ({
  content,
  className,
  popupClassName,
  contentClassName,
  id: idProp,
  iconSize = 16,
  side = 'bottom',
  align = 'center',
  sideOffset = 10,
  mobileSide,
  mobileAlign,
  mobileSideOffset,
  showArrow = true,
  children,
}: TooltipProps) => {
  const { t } = useTranslation('common');
  const generatedId = useId();
  const tooltipId = idProp ?? generatedId;
  const [open, setOpen] = useState(false);
  const isMobile = useBreakpoint(Breakpoints.sm);
  const resolvedSide = isMobile && mobileSide ? mobileSide : side;
  const resolvedAlign = isMobile && mobileAlign ? mobileAlign : align;
  const resolvedSideOffset =
    isMobile && mobileSideOffset !== undefined ? mobileSideOffset : sideOffset;
  const handleOpenChange = (nextOpen: boolean) => {
    // On mobile the trigger button drives opening/closing via its own onClick;
    // here we only honour Base UI dismissals (tapping outside, Escape, a sibling
    // tooltip opening) so the tooltip doesn't linger and several can't stay open
    // at once. Ignore open events on mobile to avoid double-toggling the tap.
    if (isMobile) {
      if (!nextOpen) setOpen(false);
      return;
    }
    setOpen((prev) => (nextOpen && prev ? false : nextOpen));
  };

  return (
    <BaseTooltip.Root open={open} onOpenChange={handleOpenChange}>
      <span className={cn(styles.wrapper, className)}>
        <BaseTooltip.Trigger
          delay={0}
          closeDelay={150}
          render={(props) => (
            <button
              type="button"
              {...props}
              className={styles.iconButton}
              aria-label={t('tooltip.moreInformation')}
              onClick={() => setOpen((prev) => !prev)}
              onKeyDown={() => setOpen((prev) => !prev)}
            >
              {children ?? <InfoIcon size={iconSize} />}
            </button>
          )}
        />
      </span>
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          side={resolvedSide}
          align={resolvedAlign}
          sideOffset={resolvedSideOffset}
        >
          <BaseTooltip.Popup
            id={tooltipId}
            className={cn(styles.bubble, popupClassName)}
            role="tooltip"
          >
            <div className={cn(styles.content, contentClassName)}>
              {content}
            </div>
            {showArrow && (
              <BaseTooltip.Arrow className={styles.arrow}>
                <IconWrapper icon="pointer" />
              </BaseTooltip.Arrow>
            )}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
